import type {
  Account,
  Approval,
  Beneficiary,
  Card,
  Iban,
  Payment,
  PaymentAttempt,
  SmeCompanyView,
  Transaction,
  User,
} from '@shared/contracts/entities';
import type { PayeeVerificationResult } from '@shared/contracts/statuses';
import type { ApiResponse } from '@shared/contracts/mockApi';
import type { Notification } from '@/fixtures';
import { db } from './db';
import { assertAiAvailable, endpoint, etagOf } from './transport';
import { ERROR_CODES, MockApiError } from './errors';
import { nowIso, nowMs } from '@/services/clock';
import { recordAudit } from '@/services/audit';
import { once } from '@/services/idempotency';

/**
 * Mock Tribe Adapter.
 *
 * Shape follows shared/07_MOCK_API_CONTRACT and the canonical domain model.
 * Every response uses the shared envelope; every failure uses a canonical code.
 * No endpoint invents provider capability that the package does not confirm.
 */

/* ============================================================== session === */

export type SessionState = {
  readonly userId: string;
  readonly companyId: string;
  /** SCA satisfied at this instant; challenges expire. */
  readonly scaSatisfiedAt: string | null;
  readonly startedAt: string;
};

const SCA_VALIDITY_MINUTES = 15;
const SESSION_VALIDITY_MINUTES = 60;

export type LoginResult = {
  readonly user: User;
  /** Login always requires SCA before a session becomes usable. */
  readonly scaRequired: true;
  readonly challengeId: string;
  readonly method: 'authenticator-app' | 'sms-otp';
};

let challengeCounter = 0;
const activeChallenges = new Map<
  string,
  { userId: string; issuedAt: number; attempts: number; purpose: string }
>();

/** The deterministic demo code. Never a real credential. */
export const DEMO_SCA_CODE = '481920';
const MAX_SCA_ATTEMPTS = 3;
const SCA_CHALLENGE_TTL_MINUTES = 5;

export function login(email: string, password: string): Promise<ApiResponse<LoginResult>> {
  return endpoint(() => {
    if (!email.trim()) {
      throw new MockApiError(ERROR_CODES.VALIDATION_ERROR, 'Enter your email address.', {
        fieldErrors: { email: 'Enter your email address.' },
      });
    }
    if (!password) {
      throw new MockApiError(ERROR_CODES.VALIDATION_ERROR, 'Enter your password.', {
        fieldErrors: { password: 'Enter your password.' },
      });
    }
    const user = db().users.find((u) => u.email.toLowerCase() === email.trim().toLowerCase());
    // Identical message for unknown user and wrong password: never disclose
    // which accounts exist.
    if (!user || password !== 'demo') {
      throw new MockApiError(
        ERROR_CODES.VALIDATION_ERROR,
        'We could not sign you in. Check your details and try again.',
      );
    }
    if (user.status === 'suspended') {
      throw new MockApiError(
        ERROR_CODES.PERMISSION_DENIED,
        'This account is suspended. Contact your administrator.',
      );
    }
    challengeCounter += 1;
    const challengeId = `sca_${String(challengeCounter).padStart(6, '0')}`;
    activeChallenges.set(challengeId, {
      userId: user.id,
      issuedAt: nowMs(),
      attempts: 0,
      purpose: 'login',
    });
    return { user, scaRequired: true as const, challengeId, method: 'authenticator-app' as const };
  }, { bypassScenario: false });
}

/** Issue a step-up challenge for a sensitive action (reveal, approve, submit). */
export function requestStepUp(userId: string, purpose: string): Promise<ApiResponse<{ challengeId: string }>> {
  return endpoint(() => {
    challengeCounter += 1;
    const challengeId = `sca_${String(challengeCounter).padStart(6, '0')}`;
    activeChallenges.set(challengeId, { userId, issuedAt: nowMs(), attempts: 0, purpose });
    return { challengeId };
  });
}

export function verifySca(challengeId: string, code: string): Promise<ApiResponse<{ verifiedAt: string; purpose: string }>> {
  return endpoint(() => {
    const challenge = activeChallenges.get(challengeId);
    if (!challenge) {
      throw new MockApiError(
        ERROR_CODES.SCA_FAILED,
        'This security check is no longer valid. Start again.',
      );
    }
    const ageMinutes = (nowMs() - challenge.issuedAt) / 60_000;
    if (ageMinutes > SCA_CHALLENGE_TTL_MINUTES) {
      activeChallenges.delete(challengeId);
      throw new MockApiError(
        ERROR_CODES.SCA_FAILED,
        'Your security code expired. We can send a new one.',
        { retryable: true },
      );
    }
    if (code.replace(/\s/g, '') !== DEMO_SCA_CODE) {
      challenge.attempts += 1;
      if (challenge.attempts >= MAX_SCA_ATTEMPTS) {
        activeChallenges.delete(challengeId);
        throw new MockApiError(
          ERROR_CODES.SCA_FAILED,
          'Too many incorrect codes. For your security, start again.',
        );
      }
      throw new MockApiError(
        ERROR_CODES.SCA_FAILED,
        `That code was not correct. ${MAX_SCA_ATTEMPTS - challenge.attempts} attempts left.`,
        { fieldErrors: { code: 'Incorrect code.' } },
      );
    }
    activeChallenges.delete(challengeId);
    return { verifiedAt: nowIso(), purpose: challenge.purpose };
  });
}

export function sessionExpired(session: SessionState): boolean {
  return (nowMs() - new Date(session.startedAt).getTime()) / 60_000 > SESSION_VALIDITY_MINUTES;
}

export function scaStale(session: SessionState): boolean {
  if (!session.scaSatisfiedAt) return true;
  return (nowMs() - new Date(session.scaSatisfiedAt).getTime()) / 60_000 > SCA_VALIDITY_MINUTES;
}

export function getCompaniesForUser(userId: string): Promise<ApiResponse<SmeCompanyView[]>> {
  return endpoint(() => {
    const user = requireUser(userId);
    return db().companies.filter((c) => user.companyIds.includes(c.id));
  });
}

export function getUser(userId: string): Promise<ApiResponse<User>> {
  return endpoint(() => requireUser(userId));
}

function requireUser(userId: string): User {
  const user = db().users.find((u) => u.id === userId);
  if (!user) throw new MockApiError(ERROR_CODES.NOT_FOUND, 'User not found.');
  return user;
}

function requireCompany(companyId: string): SmeCompanyView {
  const company = db().companies.find((c) => c.id === companyId);
  if (!company) throw new MockApiError(ERROR_CODES.NOT_FOUND, 'Company not found.');
  return company;
}

/* ============================================================= accounts === */

/**
 * Account visibility respects both company scope and the user's own
 * `accountAccess` list — an employee sees only their expense account.
 */
function visibleAccounts(companyId: string, userId: string): Account[] {
  const user = requireUser(userId);
  const all = db().accounts.filter((a) => a.companyId === companyId);
  if (user.accountAccess.includes('*')) return all;
  return all.filter((a) => user.accountAccess.includes(a.id));
}

export function getAccounts(companyId: string, userId: string): Promise<ApiResponse<Account[]>> {
  return endpoint(() => {
    if (db().scenario === 'empty') return [];
    return visibleAccounts(companyId, userId);
  });
}

export function getAccount(
  companyId: string,
  userId: string,
  accountId: string,
): Promise<ApiResponse<Account>> {
  return endpoint(() => {
    const account = visibleAccounts(companyId, userId).find((a) => a.id === accountId);
    if (!account) throw new MockApiError(ERROR_CODES.NOT_FOUND, 'Account not found.');
    return account;
  });
}

export function getIbansForAccount(accountId: string): Promise<ApiResponse<Iban[]>> {
  return endpoint(() => db().ibans.filter((i) => i.accountIds.includes(accountId)));
}

export function getIbans(companyId: string, userId: string): Promise<ApiResponse<Iban[]>> {
  return endpoint(() => {
    const ids = new Set(visibleAccounts(companyId, userId).map((a) => a.id));
    return db().ibans.filter((i) => i.accountIds.some((id) => ids.has(id)));
  });
}

export function renameAccount(
  companyId: string,
  userId: string,
  accountId: string,
  displayName: string,
): Promise<ApiResponse<Account>> {
  return endpoint(
    () => {
      const trimmed = displayName.trim();
      if (trimmed.length < 2 || trimmed.length > 40) {
        throw new MockApiError(ERROR_CODES.VALIDATION_ERROR, 'Use between 2 and 40 characters.', {
          fieldErrors: { displayName: 'Use between 2 and 40 characters.' },
        });
      }
      const account = db().accounts.find((a) => a.id === accountId && a.companyId === companyId);
      if (!account) throw new MockApiError(ERROR_CODES.NOT_FOUND, 'Account not found.');
      const before = account.displayName;
      account.displayName = trimmed;
      recordAudit({
        companyId,
        programmeId: db().programme.id,
        actorName: requireUser(userId).name,
        actorType: 'customer',
        action: 'account.renamed',
        objectType: 'account',
        objectId: accountId,
        customerVisible: true,
        summary: `Renamed account to “${trimmed}”`,
        before: { displayName: before },
        after: { displayName: trimmed },
      });
      return account;
    },
    { mutating: true },
  );
}

/**
 * Customer-initiated account status change.
 *
 * Interpretation note (recorded in build_state/DECISIONS.md): TT-R123-02 states
 * only that "an account's status can be updated". A customer surface cannot
 * grant itself arbitrary status transitions, so the permitted set is limited to
 * the two a customer legitimately controls — freezing outgoing activity and
 * lifting that freeze. Operator-imposed states are never reversible from here.
 */
export type CustomerAccountAction = 'freeze' | 'unfreeze';

export function allowedAccountActions(account: Account): readonly CustomerAccountAction[] {
  if (account.status === 'active') return ['freeze'];
  // Only a freeze the customer applied may be lifted by the customer. A
  // suspension imposed by the bank carries a statusReason and stays locked.
  if (account.status === 'restricted' && account.statusReason?.startsWith('You froze')) {
    return ['unfreeze'];
  }
  return [];
}

export function updateAccountStatus(
  companyId: string,
  userId: string,
  accountId: string,
  action: CustomerAccountAction,
  idempotencyKey: string,
): Promise<ApiResponse<Account>> {
  return endpoint(
    () =>
      once(idempotencyKey, () => {
        const account = db().accounts.find((a) => a.id === accountId && a.companyId === companyId);
        if (!account) throw new MockApiError(ERROR_CODES.NOT_FOUND, 'Account not found.');
        if (!allowedAccountActions(account).includes(action)) {
          throw new MockApiError(
            ERROR_CODES.ALREADY_ACTIONED,
            'That change is not available for this account.',
          );
        }
        const before = { status: account.status, statusReason: account.statusReason };
        if (action === 'freeze') {
          account.status = 'restricted';
          account.statusReason =
            'You froze outgoing payments on this account. It can still receive money.';
        } else {
          account.status = 'active';
          delete account.statusReason;
        }
        recordAudit({
          companyId,
          programmeId: db().programme.id,
          actorName: requireUser(userId).name,
          actorType: 'customer',
          action: `account.${action}`,
          objectType: 'account',
          objectId: accountId,
          customerVisible: true,
          summary:
            action === 'freeze'
              ? `Froze outgoing payments on ${account.displayName}`
              : `Unfroze ${account.displayName}`,
          permissionUsed: 'sme.accounts.viewSensitiveDetails',
          before,
          after: { status: account.status, statusReason: account.statusReason },
        });
        return Promise.resolve(account);
      }),
    { mutating: true },
  );
}

/* ========================================================= transactions === */

export type TransactionFilters = {
  readonly accountId?: string;
  readonly search?: string;
  readonly direction?: 'in' | 'out';
  readonly status?: Transaction['status'];
  readonly fromIso?: string;
  readonly toIso?: string;
};

export type TransactionPage = {
  readonly items: readonly Transaction[];
  /** Cursor pagination: no fabricated total page count. */
  readonly nextCursor: string | null;
  readonly totalKnown: number;
};

export function getTransactions(
  companyId: string,
  userId: string,
  filters: TransactionFilters = {},
  cursor: string | null = null,
  pageSize = 20,
): Promise<ApiResponse<TransactionPage>> {
  return endpoint(() => {
    if (db().scenario === 'empty') {
      return { items: [], nextCursor: null, totalKnown: 0 };
    }
    const accountIds = new Set(visibleAccounts(companyId, userId).map((a) => a.id));
    let rows = db()
      .transactions.filter((t) => t.companyId === companyId && accountIds.has(t.accountId))
      .sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));

    if (filters.accountId) rows = rows.filter((t) => t.accountId === filters.accountId);
    if (filters.direction) rows = rows.filter((t) => t.direction === filters.direction);
    if (filters.status) rows = rows.filter((t) => t.status === filters.status);
    if (filters.fromIso) rows = rows.filter((t) => t.occurredAt >= filters.fromIso!);
    if (filters.toIso) rows = rows.filter((t) => t.occurredAt <= filters.toIso!);
    if (filters.search) {
      const q = filters.search.trim().toLowerCase();
      rows = rows.filter(
        (t) =>
          t.counterparty.toLowerCase().includes(q) ||
          (t.reference?.toLowerCase().includes(q) ?? false) ||
          (t.merchantName?.toLowerCase().includes(q) ?? false),
      );
    }

    const start = cursor ? Math.max(0, rows.findIndex((t) => t.id === cursor)) : 0;
    const items = rows.slice(start, start + pageSize);
    const nextIndex = start + pageSize;
    const next = rows[nextIndex];
    return {
      items,
      nextCursor: next ? next.id : null,
      totalKnown: rows.length,
    };
  });
}

export function getTransaction(
  companyId: string,
  userId: string,
  transactionId: string,
): Promise<ApiResponse<Transaction>> {
  return endpoint(() => {
    const accountIds = new Set(visibleAccounts(companyId, userId).map((a) => a.id));
    const tx = db().transactions.find(
      (t) => t.id === transactionId && t.companyId === companyId && accountIds.has(t.accountId),
    );
    if (!tx) throw new MockApiError(ERROR_CODES.NOT_FOUND, 'Transaction not found.');
    return tx;
  });
}

/* ======================================================== beneficiaries === */

export function getBeneficiaries(
  companyId: string,
  search?: string,
): Promise<ApiResponse<Beneficiary[]>> {
  return endpoint(() => {
    if (db().scenario === 'empty') return [];
    let rows = db().beneficiaries.filter((b) => b.companyId === companyId);
    if (search?.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter((b) => b.name.toLowerCase().includes(q));
    }
    return rows.sort((a, b) => a.name.localeCompare(b.name));
  });
}

export function getBeneficiary(
  companyId: string,
  beneficiaryId: string,
): Promise<ApiResponse<Beneficiary>> {
  return endpoint(() => {
    const b = db().beneficiaries.find((x) => x.id === beneficiaryId && x.companyId === companyId);
    if (!b) throw new MockApiError(ERROR_CODES.NOT_FOUND, 'Beneficiary not found.');
    return b;
  });
}

export type NewBeneficiaryInput = {
  readonly name: string;
  readonly type: Beneficiary['type'];
  readonly country: string;
  readonly currency: string;
  readonly accountNumber?: string;
  readonly sortCode?: string;
  readonly iban?: string;
};

/**
 * Payee verification (Confirmation of Payee).
 *
 * Deterministic and honest: CoP is a UK domestic scheme, so a non-GB payee
 * returns `unavailable` rather than a fabricated match.
 */
export function verifyPayee(input: NewBeneficiaryInput): Promise<ApiResponse<{
  result: PayeeVerificationResult;
  matchedName?: string;
  checkedAt: string;
}>> {
  return endpoint(() => {
    if (input.country !== 'GB') {
      return { result: 'unavailable' as const, checkedAt: nowIso() };
    }
    const name = input.name.trim().toLowerCase();
    if (name.startsWith('j.') || name.includes('fairweather')) {
      return {
        result: 'close-match' as const,
        matchedName: 'John Fairweather Haulage Limited',
        checkedAt: nowIso(),
      };
    }
    if (name.includes('delta') || (input.accountNumber ?? '').startsWith('1122')) {
      return { result: 'no-match' as const, checkedAt: nowIso() };
    }
    return { result: 'match' as const, matchedName: input.name.trim(), checkedAt: nowIso() };
  });
}

export function createBeneficiary(
  companyId: string,
  userId: string,
  input: NewBeneficiaryInput,
  verification: PayeeVerificationResult,
  idempotencyKey: string,
): Promise<ApiResponse<Beneficiary>> {
  return endpoint(
    () =>
      once(idempotencyKey, () => {
        const fieldErrors: Record<string, string> = {};
        if (!input.name.trim()) fieldErrors['name'] = 'Enter the beneficiary name.';
        if (input.country === 'GB') {
          if (!/^\d{8}$/.test((input.accountNumber ?? '').replace(/\D/g, ''))) {
            fieldErrors['accountNumber'] = 'Enter the 8-digit account number.';
          }
          if (!/^\d{6}$/.test((input.sortCode ?? '').replace(/\D/g, ''))) {
            fieldErrors['sortCode'] = 'Enter the 6-digit sort code.';
          }
        } else if (!(input.iban ?? '').trim()) {
          fieldErrors['iban'] = 'Enter the IBAN.';
        }
        if (Object.keys(fieldErrors).length > 0) {
          throw new MockApiError(ERROR_CODES.VALIDATION_ERROR, 'Check the highlighted fields.', {
            fieldErrors,
          });
        }

        const duplicate = db().beneficiaries.find(
          (b) =>
            b.companyId === companyId &&
            b.name.trim().toLowerCase() === input.name.trim().toLowerCase() &&
            b.accountNumber === input.accountNumber,
        );
        if (duplicate) {
          throw new MockApiError(
            ERROR_CODES.VALIDATION_ERROR,
            'A beneficiary with these details already exists.',
          );
        }

        const beneficiary: Beneficiary = {
          id: `ben_new_${db().beneficiaries.length + 1}`,
          companyId,
          name: input.name.trim(),
          type: input.type,
          country: input.country,
          currency: input.currency,
          verificationResult: verification,
          verificationCheckedAt: nowIso(),
          status: 'active',
          ...(input.accountNumber ? { accountNumber: input.accountNumber } : {}),
          ...(input.sortCode ? { sortCode: input.sortCode } : {}),
          ...(input.iban ? { iban: input.iban } : {}),
        };
        db().beneficiaries.push(beneficiary);
        recordAudit({
          companyId,
          programmeId: db().programme.id,
          actorName: requireUser(userId).name,
          actorType: 'customer',
          action: 'beneficiary.created',
          objectType: 'beneficiary',
          objectId: beneficiary.id,
          customerVisible: true,
          summary: `Added beneficiary ${beneficiary.name}`,
          after: { name: beneficiary.name, verificationResult: verification },
        });
        return Promise.resolve(beneficiary);
      }),
    { mutating: true },
  );
}

/* ============================================================= payments === */

export function getPayments(companyId: string): Promise<ApiResponse<Payment[]>> {
  return endpoint(() =>
    db()
      .payments.filter((p) => p.companyId === companyId)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
  );
}

export function getPayment(companyId: string, paymentId: string): Promise<ApiResponse<Payment>> {
  return endpoint(() => {
    const p = db().payments.find((x) => x.id === paymentId && x.companyId === companyId);
    if (!p) throw new MockApiError(ERROR_CODES.NOT_FOUND, 'Payment not found.');
    return p;
  });
}

export function getPaymentAttempts(paymentId: string): Promise<ApiResponse<PaymentAttempt[]>> {
  return endpoint(() =>
    db()
      .paymentAttempts.filter((a) => a.paymentId === paymentId)
      .sort((a, b) => a.attemptNumber - b.attemptNumber),
  );
}

export type PaymentDraftInput = {
  readonly sourceAccountId: string;
  readonly beneficiaryId: string;
  /** Minor units. */
  readonly amount: number;
  readonly currency: string;
  readonly reference: string;
  readonly noteToBeneficiary?: string;
  readonly requestedExecutionDate: string;
};

export type PaymentValidation = {
  readonly fieldErrors: Readonly<Record<string, string>>;
  readonly businessWarnings: readonly string[];
  /** Approval is a policy outcome, decided here, not by the UI. */
  readonly approvalRequired: boolean;
  readonly approvalThreshold: number | null;
  readonly payeeVerification: PayeeVerificationResult;
  readonly valid: boolean;
};

/** Company approval policy: anything at or above this needs a second person. */
const APPROVAL_THRESHOLD_MINOR = 1_000_000;

export function validatePayment(
  companyId: string,
  userId: string,
  input: PaymentDraftInput,
): Promise<ApiResponse<PaymentValidation>> {
  return endpoint(() => {
    const fieldErrors: Record<string, string> = {};
    const businessWarnings: string[] = [];

    const account = visibleAccounts(companyId, userId).find((a) => a.id === input.sourceAccountId);
    const beneficiary = db().beneficiaries.find((b) => b.id === input.beneficiaryId);

    if (!account) fieldErrors['sourceAccountId'] = 'Choose an account to pay from.';
    if (!beneficiary) fieldErrors['beneficiaryId'] = 'Choose who you are paying.';

    if (!Number.isInteger(input.amount) || input.amount <= 0) {
      fieldErrors['amount'] = 'Enter an amount greater than zero.';
    }
    if (!input.reference.trim()) {
      fieldErrors['reference'] = 'Enter a payment reference.';
    } else if (input.reference.trim().length > 18) {
      fieldErrors['reference'] = 'References can be up to 18 characters.';
    } else if (!/^[A-Za-z0-9 ./&-]+$/.test(input.reference.trim())) {
      fieldErrors['reference'] = 'Use letters, numbers, spaces and . / & - only.';
    }

    if (account) {
      if (account.status === 'suspended' || account.status === 'closed') {
        fieldErrors['sourceAccountId'] =
          account.statusReason ?? 'You cannot send money from this account.';
      } else if (account.status === 'restricted') {
        fieldErrors['sourceAccountId'] =
          account.statusReason ?? 'This account can receive money but cannot send it.';
      } else if (account.status === 'pending') {
        fieldErrors['sourceAccountId'] = 'This account is not open yet.';
      }
      if (account.currency !== input.currency) {
        fieldErrors['currency'] = `This account holds ${account.currency}.`;
      }
      // Available, not booked, is what can actually be spent.
      if (!fieldErrors['amount'] && input.amount > account.availableBalance) {
        fieldErrors['amount'] = 'This is more than the available balance on this account.';
        if (input.amount <= account.balance) {
          businessWarnings.push(
            'Some of this balance is reserved for pending transactions, so it is not available yet.',
          );
        }
      }
    }

    if (beneficiary) {
      if (beneficiary.status === 'disabled') {
        fieldErrors['beneficiaryId'] = 'This beneficiary is disabled and cannot be paid.';
      }
      if (beneficiary.verificationResult === 'no-match') {
        businessWarnings.push(
          'The name you gave does not match the name on the account. Check with the payee before sending — payments to the wrong account may not be recoverable.',
        );
      } else if (beneficiary.verificationResult === 'close-match') {
        businessWarnings.push(
          'The name is close but not an exact match to the account holder. Confirm the details before sending.',
        );
      } else if (beneficiary.verificationResult === 'unavailable') {
        businessWarnings.push(
          'Account name checking is not available for this destination, so we cannot confirm the payee name.',
        );
      }
      const priorPayments = db().payments.filter(
        (p) => p.beneficiaryId === beneficiary.id && p.status === 'completed',
      );
      if (priorPayments.length === 0) {
        businessWarnings.push('This is the first payment to this beneficiary.');
      }
    }

    const approvalRequired = input.amount >= APPROVAL_THRESHOLD_MINOR;

    return {
      fieldErrors,
      businessWarnings,
      approvalRequired,
      approvalThreshold: approvalRequired ? APPROVAL_THRESHOLD_MINOR : null,
      payeeVerification: beneficiary?.verificationResult ?? 'unavailable',
      valid: Object.keys(fieldErrors).length === 0,
    };
  });
}

export type SubmitPaymentResult = {
  readonly payment: Payment;
  readonly attempt: PaymentAttempt;
  readonly outcome: 'completed' | 'awaiting-approval' | 'processing' | 'failed';
};

export function submitPayment(
  companyId: string,
  userId: string,
  input: PaymentDraftInput,
  idempotencyKey: string,
): Promise<ApiResponse<SubmitPaymentResult>> {
  return endpoint(
    () =>
      once(idempotencyKey, async () => {
        const validation = (await validatePayment(companyId, userId, input)).data;
        if (!validation.valid) {
          throw new MockApiError(ERROR_CODES.VALIDATION_ERROR, 'Check the highlighted fields.', {
            fieldErrors: { ...validation.fieldErrors },
          });
        }

        const id = `pay_new_${db().payments.length + 1}`;
        const approvalRequired = validation.approvalRequired;
        const beneficiary = db().beneficiaries.find((b) => b.id === input.beneficiaryId);

        // The failed-payment scenario forces a provider rejection so the
        // failure and retry paths are demonstrable on demand.
        const forcedFailure = db().scenario === 'failed-payment';
        const forcedApproval = db().scenario === 'pending-approval';

        const status: Payment['status'] = forcedFailure
          ? 'failed'
          : approvalRequired || forcedApproval
            ? 'awaiting-approval'
            : 'completed';

        const payment: Payment = {
          id,
          companyId,
          sourceAccountId: input.sourceAccountId,
          beneficiaryId: input.beneficiaryId,
          amount: input.amount,
          currency: input.currency,
          reference: input.reference.trim(),
          requestedExecutionDate: input.requestedExecutionDate,
          status,
          approvalIds: [],
          createdBy: userId,
          createdAt: nowIso(),
          ...(input.noteToBeneficiary ? { noteToBeneficiary: input.noteToBeneficiary } : {}),
          ...(beneficiary
            ? {
                payeeVerification: {
                  result: beneficiary.verificationResult,
                  checkedAt: beneficiary.verificationCheckedAt ?? nowIso(),
                  ...(beneficiary.verificationResult !== 'match'
                    ? { proceededDespiteWarning: true }
                    : {}),
                },
              }
            : {}),
          ...(forcedFailure
            ? {
                failureReason:
                  'The receiving bank rejected the payment. No money has left your account.',
              }
            : {}),
        };

        const attempt: PaymentAttempt = {
          id: `att_${id}_1`,
          paymentId: id,
          attemptNumber: 1,
          status: forcedFailure ? 'failed' : status === 'completed' ? 'succeeded' : 'queued',
          providerReference: `TRB-ATT-${90_000 + db().paymentAttempts.length}`,
          startedAt: nowIso(),
          ...(status === 'completed' || forcedFailure ? { completedAt: nowIso() } : {}),
          ...(forcedFailure ? { failureReason: 'Rejected by the receiving bank.' } : {}),
        };

        db().payments.push(payment);
        db().paymentAttempts.push(attempt);

        if (status === 'awaiting-approval') {
          const approval: Approval = {
            id: `apr_${id}`,
            objectType: 'payment',
            objectId: id,
            status: 'pending',
            requiredApprovals: 1,
            receivedApprovals: 0,
            approverIds: db()
              .users.filter((u) => u.permissions.includes('sme.payments.approve'))
              .map((u) => u.id),
            ...(validation.approvalThreshold !== null
              ? { thresholdAmount: validation.approvalThreshold }
              : {}),
          };
          db().approvals.push(approval);
          payment.approvalIds.push(approval.id);
        }

        if (status === 'completed') {
          debitAccount(input.sourceAccountId, input.amount);
          pushTransactionForPayment(payment, beneficiary?.name ?? 'Payment');
        }

        recordAudit({
          companyId,
          programmeId: db().programme.id,
          actorName: requireUser(userId).name,
          actorType: 'customer',
          action: `payment.${status === 'failed' ? 'failed' : 'submitted'}`,
          objectType: 'payment',
          objectId: id,
          customerVisible: true,
          summary:
            status === 'awaiting-approval'
              ? `Payment sent for approval`
              : status === 'failed'
                ? `Payment failed`
                : `Payment sent`,
          permissionUsed: 'sme.payments.submit',
          after: { status, amount: payment.amount, currency: payment.currency },
        });

        return {
          payment,
          attempt,
          outcome: status === 'awaiting-approval' ? ('awaiting-approval' as const) : status === 'failed' ? ('failed' as const) : ('completed' as const),
        };
      }),
    { mutating: true },
  );
}

/**
 * Retry is a NEW attempt against the same payment, never a silent status
 * mutation — 07_STATE_MACHINE_AND_EDGE_CASE_CATALOG.md requires the history.
 */
export function retryPayment(
  companyId: string,
  userId: string,
  paymentId: string,
  idempotencyKey: string,
): Promise<ApiResponse<SubmitPaymentResult>> {
  return endpoint(
    () =>
      once(idempotencyKey, () => {
        const payment = db().payments.find((p) => p.id === paymentId && p.companyId === companyId);
        if (!payment) throw new MockApiError(ERROR_CODES.NOT_FOUND, 'Payment not found.');
        if (payment.status !== 'failed' && payment.status !== 'returned') {
          throw new MockApiError(
            ERROR_CODES.ALREADY_ACTIONED,
            'Only a failed or returned payment can be retried.',
          );
        }
        const priorAttempts = db().paymentAttempts.filter((a) => a.paymentId === paymentId);
        const succeed = db().scenario !== 'failed-payment';
        const attempt: PaymentAttempt = {
          id: `att_${paymentId}_${priorAttempts.length + 1}`,
          paymentId,
          attemptNumber: priorAttempts.length + 1,
          status: succeed ? 'succeeded' : 'failed',
          providerReference: `TRB-ATT-${90_000 + db().paymentAttempts.length}`,
          startedAt: nowIso(),
          completedAt: nowIso(),
          ...(succeed ? {} : { failureReason: 'Rejected by the receiving bank again.' }),
        };
        db().paymentAttempts.push(attempt);

        if (succeed) {
          payment.status = 'completed';
          delete (payment as { failureReason?: string }).failureReason;
          debitAccount(payment.sourceAccountId, payment.amount);
          const beneficiary = db().beneficiaries.find((b) => b.id === payment.beneficiaryId);
          pushTransactionForPayment(payment, beneficiary?.name ?? 'Payment');
        }

        recordAudit({
          companyId,
          programmeId: db().programme.id,
          actorName: requireUser(userId).name,
          actorType: 'customer',
          action: 'payment.retried',
          objectType: 'payment',
          objectId: paymentId,
          customerVisible: true,
          summary: `Retried payment (attempt ${attempt.attemptNumber})`,
          after: { status: payment.status, attemptNumber: attempt.attemptNumber },
        });

        return Promise.resolve({
          payment,
          attempt,
          outcome: succeed ? ('completed' as const) : ('failed' as const),
        });
      }),
    { mutating: true },
  );
}

function debitAccount(accountId: string, amount: number): void {
  const account = db().accounts.find((a) => a.id === accountId);
  if (!account) return;
  account.balance -= amount;
  account.availableBalance -= amount;
}

function pushTransactionForPayment(payment: Payment, counterparty: string): void {
  db().transactions.unshift({
    id: `tx_${payment.id}`,
    companyId: payment.companyId,
    accountId: payment.sourceAccountId,
    type: 'bank-transfer',
    direction: 'out',
    amount: payment.amount,
    currency: payment.currency,
    status: 'completed',
    counterparty,
    reference: payment.reference,
    occurredAt: nowIso(),
    bookedAt: nowIso(),
    relatedPaymentId: payment.id,
  });
}

/* ================================================ account-to-account ===== */

export type TransferInput = {
  readonly sourceAccountId: string;
  readonly destinationAccountId: string;
  /** Minor units, in the source account's currency. */
  readonly amount: number;
  readonly reference: string;
};

export type TransferValidation = {
  readonly fieldErrors: Readonly<Record<string, string>>;
  readonly warnings: readonly string[];
  readonly valid: boolean;
};

export function validateTransfer(
  companyId: string,
  userId: string,
  input: TransferInput,
): Promise<ApiResponse<TransferValidation>> {
  return endpoint(() => {
    const fieldErrors: Record<string, string> = {};
    const warnings: string[] = [];
    const accounts = visibleAccounts(companyId, userId);
    const from = accounts.find((a) => a.id === input.sourceAccountId);
    const to = accounts.find((a) => a.id === input.destinationAccountId);

    if (!from) fieldErrors['sourceAccountId'] = 'Choose an account to move money from.';
    if (!to) fieldErrors['destinationAccountId'] = 'Choose an account to move money to.';
    if (from && to && from.id === to.id) {
      fieldErrors['destinationAccountId'] = 'Choose a different account to move money to.';
    }
    if (from && to && from.currency !== to.currency) {
      // A cross-currency move is an FX conversion, not a transfer, and needs a
      // dealable quote the provider has not confirmed.
      fieldErrors['destinationAccountId'] =
        `Moving ${from.currency} into a ${to.currency} account needs a currency exchange.`;
    }
    if (from && (from.status === 'suspended' || from.status === 'closed' || from.status === 'restricted')) {
      fieldErrors['sourceAccountId'] = from.statusReason ?? 'You cannot move money out of this account.';
    }
    if (to && (to.status === 'closed' || to.status === 'suspended')) {
      fieldErrors['destinationAccountId'] = to.statusReason ?? 'This account cannot receive money.';
    }
    if (!Number.isInteger(input.amount) || input.amount <= 0) {
      fieldErrors['amount'] = 'Enter an amount greater than zero.';
    } else if (from && input.amount > from.availableBalance) {
      fieldErrors['amount'] = 'This is more than the available balance on that account.';
      if (input.amount <= from.balance) {
        warnings.push('Some of this balance is reserved for pending transactions.');
      }
    }

    return { fieldErrors, warnings, valid: Object.keys(fieldErrors).length === 0 };
  });
}

export type TransferResult = {
  readonly from: Account;
  readonly to: Account;
  readonly amount: number;
  readonly currency: string;
  readonly reference: string;
};

/**
 * Move money between the company's own accounts.
 *
 * Both legs are written as separate canonical transactions so each account's
 * history is truthful on its own, and one audit event records the movement.
 */
export function transferBetweenAccounts(
  companyId: string,
  userId: string,
  input: TransferInput,
  idempotencyKey: string,
): Promise<ApiResponse<TransferResult>> {
  return endpoint(
    () =>
      once(idempotencyKey, async () => {
        const validation = (await validateTransfer(companyId, userId, input)).data;
        if (!validation.valid) {
          throw new MockApiError(ERROR_CODES.VALIDATION_ERROR, 'Check the highlighted fields.', {
            fieldErrors: { ...validation.fieldErrors },
          });
        }
        const from = db().accounts.find((a) => a.id === input.sourceAccountId)!;
        const to = db().accounts.find((a) => a.id === input.destinationAccountId)!;

        from.balance -= input.amount;
        from.availableBalance -= input.amount;
        to.balance += input.amount;
        to.availableBalance += input.amount;

        const at = nowIso();
        const reference = input.reference.trim() || 'Transfer';
        db().transactions.unshift(
          {
            id: `tx_tfr_out_${db().transactions.length + 1}`,
            companyId,
            accountId: from.id,
            type: 'bank-transfer',
            direction: 'out',
            amount: input.amount,
            currency: from.currency,
            status: 'completed',
            counterparty: `Transfer to ${to.displayName}`,
            reference,
            occurredAt: at,
            bookedAt: at,
          },
          {
            id: `tx_tfr_in_${db().transactions.length + 2}`,
            companyId,
            accountId: to.id,
            type: 'bank-transfer',
            direction: 'in',
            amount: input.amount,
            currency: to.currency,
            status: 'completed',
            counterparty: `Transfer from ${from.displayName}`,
            reference,
            occurredAt: at,
            bookedAt: at,
          },
        );

        recordAudit({
          companyId,
          programmeId: db().programme.id,
          actorName: requireUser(userId).name,
          actorType: 'customer',
          action: 'transfer.completed',
          objectType: 'account',
          objectId: from.id,
          customerVisible: true,
          summary: `Moved money from ${from.displayName} to ${to.displayName}`,
          permissionUsed: 'sme.payments.submit',
          after: { amount: input.amount, currency: from.currency, to: to.id },
        });

        return {
          from,
          to,
          amount: input.amount,
          currency: from.currency,
          reference,
        };
      }),
    { mutating: true },
  );
}

/* ============================================================ approvals === */

export type ApprovalListItem = {
  readonly approval: Approval;
  readonly payment: Payment;
  readonly beneficiary: Beneficiary | undefined;
  readonly sourceAccount: Account | undefined;
};

export function getPendingApprovals(
  companyId: string,
  userId: string,
): Promise<ApiResponse<ApprovalListItem[]>> {
  return endpoint(() => {
    if (db().scenario === 'empty') return [];
    return db()
      .approvals.filter((a) => a.status === 'pending' && a.approverIds.includes(userId))
      .map((approval) => {
        const payment = db().payments.find((p) => p.id === approval.objectId);
        return payment && payment.companyId === companyId
          ? {
              approval,
              payment,
              beneficiary: db().beneficiaries.find((b) => b.id === payment.beneficiaryId),
              sourceAccount: db().accounts.find((a) => a.id === payment.sourceAccountId),
            }
          : null;
      })
      .filter((x): x is ApprovalListItem => x !== null);
  });
}

export function getApproval(
  companyId: string,
  approvalId: string,
): Promise<ApiResponse<ApprovalListItem>> {
  return endpoint(() => {
    const approval = db().approvals.find((a) => a.id === approvalId);
    if (!approval) throw new MockApiError(ERROR_CODES.NOT_FOUND, 'Approval not found.');
    const payment = db().payments.find((p) => p.id === approval.objectId);
    if (!payment || payment.companyId !== companyId) {
      throw new MockApiError(ERROR_CODES.NOT_FOUND, 'Approval not found.');
    }
    return {
      approval,
      payment,
      beneficiary: db().beneficiaries.find((b) => b.id === payment.beneficiaryId),
      sourceAccount: db().accounts.find((a) => a.id === payment.sourceAccountId),
    };
  });
}

export function decideApproval(
  companyId: string,
  userId: string,
  approvalId: string,
  decision: 'approve' | 'reject',
  reason: string | null,
  idempotencyKey: string,
): Promise<ApiResponse<ApprovalListItem>> {
  return endpoint(
    () =>
      once(idempotencyKey, () => {
        const approval = db().approvals.find((a) => a.id === approvalId);
        if (!approval) throw new MockApiError(ERROR_CODES.NOT_FOUND, 'Approval not found.');
        if (approval.status !== 'pending') {
          throw new MockApiError(
            ERROR_CODES.ALREADY_ACTIONED,
            'This request has already been decided by someone else.',
          );
        }
        if (!approval.approverIds.includes(userId)) {
          throw new MockApiError(
            ERROR_CODES.PERMISSION_DENIED,
            'You are not an approver for this request.',
          );
        }
        if (decision === 'reject' && !reason?.trim()) {
          throw new MockApiError(ERROR_CODES.VALIDATION_ERROR, 'Give a reason for rejecting.', {
            fieldErrors: { reason: 'Give a reason for rejecting.' },
          });
        }

        const payment = db().payments.find((p) => p.id === approval.objectId);
        if (!payment) throw new MockApiError(ERROR_CODES.NOT_FOUND, 'Payment not found.');

        if (decision === 'approve') {
          approval.receivedApprovals += 1;
          if (approval.receivedApprovals >= approval.requiredApprovals) {
            approval.status = 'approved';
            payment.status = 'completed';
            debitAccount(payment.sourceAccountId, payment.amount);
            const beneficiary = db().beneficiaries.find((b) => b.id === payment.beneficiaryId);
            pushTransactionForPayment(payment, beneficiary?.name ?? 'Payment');
          }
        } else {
          approval.status = 'rejected';
          approval.rejectionReason = reason!.trim();
          payment.status = 'cancelled';
        }

        recordAudit({
          companyId,
          programmeId: db().programme.id,
          actorName: requireUser(userId).name,
          actorType: 'customer',
          // Explicit, not string-built: `'reject' + 'd'` would emit
          // "approval.rejectd" and quietly corrupt the audit vocabulary.
          action: decision === 'approve' ? 'approval.approved' : 'approval.rejected',
          objectType: 'payment',
          objectId: payment.id,
          customerVisible: true,
          summary:
            decision === 'approve'
              ? 'Approved a payment'
              : `Rejected a payment — ${reason?.trim() ?? ''}`,
          permissionUsed: 'sme.payments.approve',
          ...(reason?.trim() ? { reason: reason.trim() } : {}),
          after: { approvalStatus: approval.status, paymentStatus: payment.status },
        });

        return Promise.resolve({
          approval,
          payment,
          beneficiary: db().beneficiaries.find((b) => b.id === payment.beneficiaryId),
          sourceAccount: db().accounts.find((a) => a.id === payment.sourceAccountId),
        });
      }),
    { mutating: true },
  );
}

/* ================================================================ cards === */

export function getCards(companyId: string, userId: string): Promise<ApiResponse<Card[]>> {
  return endpoint(() => {
    if (db().scenario === 'empty') return [];
    const user = requireUser(userId);
    const all = db().cards.filter((c) => c.companyId === companyId);
    // An employee only ever sees their own card.
    if (!user.permissions.includes('sme.cards.manage') && user.roleId === 'role_employee') {
      return all.filter((c) => c.assignedUserId === userId);
    }
    return all;
  });
}

export function getCard(companyId: string, cardId: string): Promise<ApiResponse<Card>> {
  return endpoint(() => {
    const card = db().cards.find((c) => c.id === cardId && c.companyId === companyId);
    if (!card) throw new MockApiError(ERROR_CODES.NOT_FOUND, 'Card not found.');
    return card;
  });
}

export type CardAction = 'freeze' | 'unfreeze' | 'block' | 'activate' | 'request-replacement';

/** Which actions the lifecycle state permits. Operator blocks are terminal. */
export function allowedCardActions(card: Card): readonly CardAction[] {
  switch (card.lifecycleStatus) {
    case 'active':
      return ['freeze', 'block'];
    case 'frozen':
      return ['unfreeze', 'block'];
    case 'ordered':
      return ['activate', 'block'];
    case 'blocked':
      // Blocked by the bank: no customer unfreeze, only a replacement request.
      return card.replacementStatus === 'not-requested' ? ['request-replacement'] : [];
    case 'expired':
      return card.replacementStatus === 'not-requested' ? ['request-replacement'] : [];
    case 'cancelled':
      return [];
    default:
      return [];
  }
}

export function performCardAction(
  companyId: string,
  userId: string,
  cardId: string,
  action: CardAction,
  idempotencyKey: string,
): Promise<ApiResponse<Card>> {
  return endpoint(
    () =>
      once(idempotencyKey, () => {
        const card = db().cards.find((c) => c.id === cardId && c.companyId === companyId);
        if (!card) throw new MockApiError(ERROR_CODES.NOT_FOUND, 'Card not found.');
        if (!allowedCardActions(card).includes(action)) {
          throw new MockApiError(
            ERROR_CODES.ALREADY_ACTIONED,
            'That is no longer possible for this card.',
          );
        }
        const before = card.lifecycleStatus;
        switch (action) {
          case 'freeze':
            card.lifecycleStatus = 'frozen';
            card.statusReason = 'You froze this card.';
            break;
          case 'unfreeze':
            card.lifecycleStatus = 'active';
            delete card.statusReason;
            break;
          case 'block':
            card.lifecycleStatus = 'blocked';
            card.statusReason = 'You blocked this card permanently.';
            break;
          case 'activate':
            card.lifecycleStatus = 'active';
            card.fulfilmentStatus = 'delivered';
            break;
          case 'request-replacement':
            card.replacementStatus = 'requested';
            break;
        }
        recordAudit({
          companyId,
          programmeId: db().programme.id,
          actorName: requireUser(userId).name,
          actorType: 'customer',
          action: `card.${action}`,
          objectType: 'card',
          objectId: cardId,
          customerVisible: true,
          summary: `Card ending ${card.last4}: ${action.replace('-', ' ')}`,
          permissionUsed: 'sme.cards.manage',
          before: { lifecycleStatus: before, replacementStatus: card.replacementStatus },
          after: {
            lifecycleStatus: card.lifecycleStatus,
            replacementStatus: card.replacementStatus,
          },
        });
        return Promise.resolve(card);
      }),
    { mutating: true },
  );
}

export function setCardControls(
  companyId: string,
  userId: string,
  cardId: string,
  controls: Partial<Card['controls']>,
): Promise<ApiResponse<Card>> {
  return endpoint(
    () => {
      const card = db().cards.find((c) => c.id === cardId && c.companyId === companyId);
      if (!card) throw new MockApiError(ERROR_CODES.NOT_FOUND, 'Card not found.');
      if (card.lifecycleStatus === 'blocked' || card.lifecycleStatus === 'cancelled') {
        throw new MockApiError(
          ERROR_CODES.ACCOUNT_RESTRICTED,
          'Controls cannot be changed on a blocked card.',
        );
      }
      const before = { ...card.controls };
      card.controls = { ...card.controls, ...controls };
      recordAudit({
        companyId,
        programmeId: db().programme.id,
        actorName: requireUser(userId).name,
        actorType: 'customer',
        action: 'card.controls-changed',
        objectType: 'card',
        objectId: cardId,
        customerVisible: true,
        summary: `Updated spending controls on card ending ${card.last4}`,
        permissionUsed: 'sme.cards.manage',
        before,
        after: { ...card.controls },
      });
      return card;
    },
    { mutating: true },
  );
}

export function setCardLimit(
  companyId: string,
  userId: string,
  cardId: string,
  spendingLimit: number,
): Promise<ApiResponse<Card>> {
  return endpoint(
    () => {
      const card = db().cards.find((c) => c.id === cardId && c.companyId === companyId);
      if (!card) throw new MockApiError(ERROR_CODES.NOT_FOUND, 'Card not found.');
      if (!Number.isInteger(spendingLimit) || spendingLimit < 0) {
        throw new MockApiError(ERROR_CODES.VALIDATION_ERROR, 'Enter a valid limit.', {
          fieldErrors: { spendingLimit: 'Enter a valid limit.' },
        });
      }
      if (spendingLimit > 2_000_000) {
        throw new MockApiError(
          ERROR_CODES.VALIDATION_ERROR,
          'The limit for this card cannot exceed £20,000.00.',
          { fieldErrors: { spendingLimit: 'Maximum £20,000.00.' } },
        );
      }
      const before = card.spendingLimit;
      card.spendingLimit = spendingLimit;
      recordAudit({
        companyId,
        programmeId: db().programme.id,
        actorName: requireUser(userId).name,
        actorType: 'customer',
        action: 'card.limit-changed',
        objectType: 'card',
        objectId: cardId,
        customerVisible: true,
        summary: `Changed the spending limit on card ending ${card.last4}`,
        permissionUsed: 'sme.cards.manage',
        before: { spendingLimit: before },
        after: { spendingLimit },
      });
      return card;
    },
    { mutating: true },
  );
}

export type RevealedCard = {
  readonly pan: string;
  readonly expiry: string;
  readonly cvv: string;
  /** Revealed values are shown once and never cached. */
  readonly expiresInSeconds: number;
};

/**
 * Secure card-detail reveal. Requires a verified step-up challenge, and the
 * returned values are demo-only synthetic digits — never a reusable secret.
 */
export function revealCard(
  companyId: string,
  userId: string,
  cardId: string,
  verifiedChallengePurpose: string,
): Promise<ApiResponse<RevealedCard>> {
  return endpoint(() => {
    if (verifiedChallengePurpose !== `card-reveal:${cardId}`) {
      throw new MockApiError(
        ERROR_CODES.SCA_REQUIRED,
        'Confirm your identity before card details can be shown.',
      );
    }
    const card = db().cards.find((c) => c.id === cardId && c.companyId === companyId);
    if (!card) throw new MockApiError(ERROR_CODES.NOT_FOUND, 'Card not found.');
    if (card.lifecycleStatus === 'blocked' || card.lifecycleStatus === 'cancelled') {
      throw new MockApiError(
        ERROR_CODES.ACCOUNT_RESTRICTED,
        'Details cannot be shown for a blocked card.',
      );
    }
    recordAudit({
      companyId,
      programmeId: db().programme.id,
      actorName: requireUser(userId).name,
      actorType: 'customer',
      action: 'card.details-revealed',
      objectType: 'card',
      objectId: cardId,
      customerVisible: true,
      summary: `Viewed full details for card ending ${card.last4}`,
      permissionUsed: 'sme.cards.reveal',
    });
    return {
      pan: `4000 0012 3456 ${card.last4}`,
      expiry: '09/29',
      cvv: '284',
      expiresInSeconds: 30,
    };
  });
}

/* ======================================================== notifications === */

export function getNotifications(companyId: string): Promise<ApiResponse<Notification[]>> {
  return endpoint(() =>
    db()
      .notifications.filter((n) => n.companyId === companyId)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
  );
}

export function markNotificationRead(
  companyId: string,
  notificationId: string,
): Promise<ApiResponse<Notification[]>> {
  return endpoint(
    () => {
      const list = db().notifications;
      const index = list.findIndex((n) => n.id === notificationId && n.companyId === companyId);
      if (index >= 0) {
        const existing = list[index];
        if (existing) list[index] = { ...existing, read: true };
      }
      return list.filter((n) => n.companyId === companyId);
    },
    { mutating: true },
  );
}

export function markAllNotificationsRead(companyId: string): Promise<ApiResponse<Notification[]>> {
  return endpoint(
    () => {
      db().notifications = db().notifications.map((n) =>
        n.companyId === companyId ? { ...n, read: true } : n,
      );
      return db().notifications.filter((n) => n.companyId === companyId);
    },
    { mutating: true },
  );
}

/* ============================================================== reports === */

export type FinanceSummary = {
  readonly periodLabel: string;
  readonly comparisonLabel: string;
  readonly moneyInMinor: number;
  readonly moneyOutMinor: number;
  readonly netMinor: number;
  readonly currency: string;
  readonly moneyInChangeRatio: number;
  readonly moneyOutChangeRatio: number;
  readonly byCategory: readonly { readonly category: string; readonly amountMinor: number }[];
};

export function getFinanceSummary(
  companyId: string,
  userId: string,
  days: number,
): Promise<ApiResponse<FinanceSummary>> {
  return endpoint(() => {
    const accountIds = new Set(visibleAccounts(companyId, userId).map((a) => a.id));
    const company = requireCompany(companyId);
    const cutoff = new Date(nowMs() - days * 86_400_000).toISOString();
    const priorCutoff = new Date(nowMs() - days * 2 * 86_400_000).toISOString();
    const rows = db().transactions.filter(
      (t) =>
        t.companyId === companyId &&
        accountIds.has(t.accountId) &&
        t.status === 'completed' &&
        t.currency === company.baseCurrency,
    );
    const current = rows.filter((t) => t.occurredAt >= cutoff);
    const prior = rows.filter((t) => t.occurredAt >= priorCutoff && t.occurredAt < cutoff);

    const sum = (list: Transaction[], dir: 'in' | 'out') =>
      list.filter((t) => t.direction === dir).reduce((acc, t) => acc + t.amount, 0);

    const moneyIn = sum(current, 'in');
    const moneyOut = sum(current, 'out');
    const priorIn = sum(prior, 'in');
    const priorOut = sum(prior, 'out');

    const categories = new Map<string, number>();
    current
      .filter((t) => t.direction === 'out')
      .forEach((t) => {
        const key = t.category ?? 'Other';
        categories.set(key, (categories.get(key) ?? 0) + t.amount);
      });

    return {
      periodLabel: `Last ${days} days`,
      comparisonLabel: `vs previous ${days} days`,
      moneyInMinor: moneyIn,
      moneyOutMinor: moneyOut,
      netMinor: moneyIn - moneyOut,
      currency: company.baseCurrency,
      moneyInChangeRatio: priorIn === 0 ? 0 : (moneyIn - priorIn) / priorIn,
      moneyOutChangeRatio: priorOut === 0 ? 0 : (moneyOut - priorOut) / priorOut,
      byCategory: [...categories.entries()]
        .map(([category, amountMinor]) => ({ category, amountMinor }))
        .sort((a, b) => b.amountMinor - a.amountMinor),
    };
  });
}

/* ================================================================== FX ==== */

export type FxRate = {
  readonly pair: string;
  readonly from: string;
  readonly to: string;
  readonly rate: number;
  readonly asOf: string;
  /**
   * Indicative, not a dealable quote. Labelled explicitly so a converted
   * total is never mistaken for a guaranteed amount (WR-040).
   */
  readonly indicative: true;
};

/** Deterministic indicative rates against GBP. */
const RATE_TABLE: Readonly<Record<string, number>> = {
  'GBP/GBP': 1,
  'EUR/GBP': 0.8452,
  'USD/GBP': 0.7813,
  'GBP/EUR': 1.1832,
  'GBP/USD': 1.2799,
};

export function getFxRates(base: string): Promise<ApiResponse<FxRate[]>> {
  return endpoint(() => {
    const currencies = ['GBP', 'EUR', 'USD'];
    return currencies
      .filter((c) => c !== base)
      .map((from) => {
        const rate = RATE_TABLE[`${from}/${base}`] ?? 1;
        return {
          pair: `${from}/${base}`,
          from,
          to: base,
          rate,
          asOf: nowIso(),
          indicative: true as const,
        };
      });
  });
}

/**
 * Convert a minor-unit amount into the base currency using the indicative rate.
 * Returns null when no rate is known, so the UI can say so rather than guess.
 */
export function convertToBase(
  amountMinor: number,
  from: string,
  base: string,
): { minor: number; rate: number } | null {
  if (from === base) return { minor: amountMinor, rate: 1 };
  const rate = RATE_TABLE[`${from}/${base}`];
  if (rate === undefined) return null;
  return { minor: Math.round(amountMinor * rate), rate };
}

/* ==================================================================== AI === */

export type AiInsight = {
  readonly id: string;
  readonly title: string;
  readonly body: string;
  readonly confidence: 'high' | 'medium' | 'low';
  readonly sources: readonly { readonly label: string; readonly route: string }[];
  readonly generatedAt: string;
};

/**
 * AI insights are read-only explanations over data the user can already see.
 * The adapter never exposes an AI path that could move money or change a
 * regulated decision — there is deliberately no such endpoint.
 */
export function getAiInsights(
  companyId: string,
  userId: string,
): Promise<ApiResponse<AiInsight[]>> {
  return endpoint(() => {
    assertAiAvailable();
    const accountIds = new Set(visibleAccounts(companyId, userId).map((a) => a.id));
    const rows = db().transactions.filter(
      (t) => t.companyId === companyId && accountIds.has(t.accountId),
    );
    const failed = rows.filter((t) => t.status === 'failed');
    const pendingApprovals = db().approvals.filter(
      (a) => a.status === 'pending' && a.approverIds.includes(userId),
    );

    const insights: AiInsight[] = [];
    if (pendingApprovals.length > 0) {
      insights.push({
        id: 'ai_approvals',
        title: `${pendingApprovals.length} payment${pendingApprovals.length === 1 ? '' : 's'} waiting on you`,
        body: 'These are blocking other people. Reviewing them first clears the queue fastest.',
        confidence: 'high',
        sources: [{ label: 'Approvals', route: '/mobile/approvals' }],
        generatedAt: nowIso(),
      });
    }
    if (failed.length > 0) {
      insights.push({
        id: 'ai_failed',
        title: `${failed.length} payment${failed.length === 1 ? '' : 's'} failed recently`,
        body: 'Each one needs either corrected details or a fresh attempt. No money left your account.',
        confidence: 'high',
        sources: [{ label: 'Payments', route: '/mobile/payments' }],
        generatedAt: nowIso(),
      });
    }
    insights.push({
      id: 'ai_summary',
      title: 'Spending is concentrated in a few suppliers',
      body: 'Most outgoing value this month went to a small number of counterparties. Worth checking the terms you have with them.',
      confidence: 'medium',
      sources: [{ label: 'Reports', route: '/mobile/reports' }],
      generatedAt: nowIso(),
    });
    return insights;
  });
}

/* ============================================================== helpers === */

export { etagOf };
